import { Fragment } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { Check, ChevronDown } from 'lucide-react';

import { cn } from '../lib/cn';
import { useLocale } from '../hooks/useLocale';

/**
 * Per-locale display config — country flag (emoji), short code shown on the
 * trigger, and full name shown inside the dropdown panel.
 *
 * Flag mapping: canonical "most common" association.
 *   en → US flag
 *   es → Spain flag
 *   de → Germany flag
 *   fr → France flag
 *
 * Add a new locale here AND in VITE_SUPPORTED_LOCALES + backend allowedLocales.
 */
const LOCALES: Record<string, { flag: string; short: string; name: string }> = {
    en: { flag: '🇺🇸', short: 'EN', name: 'English' },
    es: { flag: '🇪🇸', short: 'ES', name: 'Español' },
    de: { flag: '🇩🇪', short: 'DE', name: 'Deutsch' },
    fr: { flag: '🇫🇷', short: 'FR', name: 'Français' },
};

interface LocalePickerProps {
    isDarkMode?: boolean;
}

/**
 * LocalePicker — header-mounted button showing the current locale's country
 * flag + short code, with a HeadlessUI Menu dropdown to switch. Lives next
 * to the Menu dropdown in the TopNavbar's right slot.
 *
 * Picks across system languages should be a one-click action — that's why
 * it's a top-level header control rather than nested inside the Menu.
 */
export default function LocalePicker({ isDarkMode = false }: LocalePickerProps) {
    const { locale, setLocale, supported } = useLocale();
    const current = LOCALES[locale] ?? { flag: '🌐', short: locale.toUpperCase(), name: locale };

    return (
        <Menu as="div" className="relative">
            {({ open }) => (
                <>
                    <Menu.Button
                        aria-label={`Language: ${current.name}`}
                        className={cn(
                            'inline-flex items-center gap-2 px-3 py-2 rounded-lg text-[13px] font-semibold border transition-colors duration-150',
                            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500',
                            open
                                ? isDarkMode
                                    ? 'bg-gray-700 border-gray-600 text-white'
                                    : 'bg-gray-100 border-gray-400 text-gray-900'
                                : isDarkMode
                                    ? 'bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700'
                                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50',
                        )}
                    >
                        <span className="text-base leading-none">{current.flag}</span>
                        <span>{current.short}</span>
                        <ChevronDown size={14} />
                    </Menu.Button>
                    <Transition
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="opacity-0 scale-95"
                        enterTo="opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="opacity-100 scale-100"
                        leaveTo="opacity-0 scale-95"
                    >
                        <Menu.Items
                            className={cn(
                                'absolute right-0 mt-2 w-48 rounded-lg border shadow-xl z-50 overflow-hidden py-1',
                                'focus-visible:outline-none',
                                isDarkMode ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-200',
                            )}
                        >
                            {supported.map((lng) => {
                                const meta = LOCALES[lng] ?? { flag: '🌐', short: lng.toUpperCase(), name: lng };
                                const selected = lng === locale;
                                return (
                                    <Menu.Item key={lng}>
                                        {({ active }) => (
                                            <button
                                                type="button"
                                                onClick={() => { void setLocale(lng); }}
                                                className={cn(
                                                    'w-full text-left px-3 py-2 text-[13px] flex items-center gap-3 transition-colors duration-100',
                                                    isDarkMode ? 'text-gray-300' : 'text-gray-700',
                                                    (active || selected) && (isDarkMode ? 'bg-gray-800' : 'bg-gray-50'),
                                                )}
                                            >
                                                <span className="text-lg leading-none">{meta.flag}</span>
                                                <span className={cn('flex-1', selected && 'font-semibold')}>{meta.name}</span>
                                                {selected && <Check size={14} className={isDarkMode ? 'text-emerald-400' : 'text-emerald-600'} />}
                                            </button>
                                        )}
                                    </Menu.Item>
                                );
                            })}
                        </Menu.Items>
                    </Transition>
                </>
            )}
        </Menu>
    );
}
